/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { DependencyList, useEffect, useState } from 'react';

export function useAsync<T>(
	task: () => Promise<T>,
	dependencies?: DependencyList,
): AsyncResult<T, Error> {
	const [result, setResult] = useState(AsyncResult.pending<T, Error>());

	useEffect(() => {
		let inEffect = true;

		task()
			.then((value) => {
				if (inEffect) {
					setResult(AsyncResult.complete(value));
				}
			})
			.catch((error) => {
				if (inEffect) {
					setResult(AsyncResult.error(error));
				}
			});

		return () => {
			inEffect = false;
		};
	}, dependencies);

	return result;
}

export abstract class AsyncResult<T, E extends Error = Error> {
	public static pending<T, E extends Error = Error>(): AsyncResult<T, E> {
		return new PendingAsyncResult();
	}

	public static complete<T, E extends Error = Error>(value: T): AsyncResult<T, E> {
		return new CompleteAsyncResult(value);
	}

	public static error<T, E extends Error>(error: E): AsyncResult<T, E> {
		return new ErrorAsyncResult(error);
	}

	public static isPending<T, E extends Error>(
		result: AsyncResult<T, E>,
	): result is PendingAsyncResult<T, E> {
		return result.isPending === true;
	}

	public static isComplete<T, E extends Error>(
		result: AsyncResult<T, E>,
	): result is CompleteAsyncResult<T, E> {
		return result.isPending === false && (result as ErrorAsyncResult<T, E>).error === undefined;
	}

	public static isError<T, E extends Error>(
		result: AsyncResult<T, E>,
	): result is ErrorAsyncResult<T, E> {
		return result.isPending === false && (result as ErrorAsyncResult<T, E>).error !== undefined;
	}

	protected constructor(public readonly isPending: boolean) {}

	abstract map<U>(mapper: (value: T) => U): AsyncResult<U, E>;

	abstract catch(catcher: (error: E) => T): AsyncResult<T, E>;

	abstract get value(): T;
}

class PendingAsyncResult<T, E extends Error> extends AsyncResult<T, E> {
	public constructor() {
		super(true);
	}

	public map<U>(): AsyncResult<U, E> {
		return new PendingAsyncResult();
	}

	public catch(): AsyncResult<T, E> {
		return this;
	}

	public get value(): T {
		throw new Error('AsyncResult is pending...');
	}
}

class CompleteAsyncResult<T, E extends Error> extends AsyncResult<T, E> {
	public constructor(public readonly value: T) {
		super(false);
	}

	public map<U>(mapper: (value: T) => U): AsyncResult<U, E> {
		return new CompleteAsyncResult(mapper(this.value));
	}

	public catch(): AsyncResult<T, E> {
		return this;
	}
}

class ErrorAsyncResult<T, E extends Error> extends AsyncResult<T, E> {
	public constructor(public readonly error: E) {
		super(false);
	}

	public map<U>(): AsyncResult<U, E> {
		return new ErrorAsyncResult(this.error);
	}

	public catch(catcher: (error: E) => T): AsyncResult<T, E> {
		return new CompleteAsyncResult(catcher(this.error));
	}

	public get value(): T {
		throw new AsyncResultError(this.error);
	}
}

export class AsyncResultError extends Error {
	public constructor(public override readonly cause: Error) {
		super(cause.message);
		this.name = AsyncResultError.name;
	}
}
